import { QueryClient, type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@hrms/shared';
import { useToast } from '../ui/Toast';
import { errorMessage } from './errors';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof HttpError && [401, 403, 404, 422].includes(err.status)) && count < 2,
    },
  },
});

/**
 * Mutation helper: invalidates query-key prefixes, shows a success toast and an error toast.
 * Query key conventions: [resource, ...params] e.g. ['leave', 'requests', 'mine', page].
 * Invalidating ['leave'] refreshes every leave query.
 */
export function useMut<V, R>(
  fn: (v: V) => Promise<R>,
  opts: {
    invalidate?: QueryKey[];
    success?: string | ((r: R, v: V) => string);
    onSuccess?: (r: R, v: V) => void;
    onError?: (e: unknown) => void;
    /** Don't toast errors (e.g. when the form shows field errors inline). */
    silentError?: boolean;
  } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<R, unknown, V>({
    mutationFn: fn,
    onSuccess: (r, v) => {
      opts.invalidate?.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(r, v) : opts.success);
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (!opts.silentError) toast.error(errorMessage(e));
      opts.onError?.(e);
    },
  });
}
